import React, { useEffect, useState } from 'react';
import { Flame, X } from 'lucide-react';
interface NotificationData {
  name: string;
  location: string;
  action: string;
  timeAgo: string;
}
const notificationPool: NotificationData[] = [{
  name: 'Sarah',
  location: 'Austin',
  action: 'started free trial',
  timeAgo: '5 min ago'
}, {
  name: 'David',
  location: 'Denver',
  action: 'closed first deal',
  timeAgo: '8 min ago'
}, {
  name: 'Jessica',
  location: 'Miami',
  action: 'sent 1,000+ offers',
  timeAgo: '12 min ago'
}, {
  name: 'Michael',
  location: 'Seattle',
  action: 'upgraded to Scale',
  timeAgo: '15 min ago'
}, {
  name: 'James',
  location: 'Nashville',
  action: 'started free trial',
  timeAgo: '22 min ago'
}, {
  name: 'Lauren',
  location: 'Charlotte',
  action: 'sent first 100 offers',
  timeAgo: '25 min ago'
}, {
  name: 'Chris',
  location: 'Atlanta',
  action: 'closed their first deal',
  timeAgo: '28 min ago'
}, {
  name: 'Ryan',
  location: 'San Diego',
  action: 'started free trial',
  timeAgo: '35 min ago'
}, {
  name: 'Nicole',
  location: 'Tampa',
  action: 'sent 500+ offers',
  timeAgo: '38 min ago'
}, {
  name: 'Brandon',
  location: 'Las Vegas',
  action: 'upgraded to Scale',
  timeAgo: '42 min ago'
}, {
  name: 'Stephanie',
  location: 'Orlando',
  action: 'closed second deal',
  timeAgo: '45 min ago'
}, {
  name: 'Rachel',
  location: 'Indianapolis',
  action: 'started free trial',
  timeAgo: '52 min ago'
}, {
  name: 'Tyler',
  location: 'Columbus',
  action: 'sent 1,000+ offers',
  timeAgo: '55 min ago'
}, {
  name: 'Melissa',
  location: 'San Antonio',
  action: 'closed first deal',
  timeAgo: '58 min ago'
}, {
  name: 'Jason',
  location: 'Jacksonville',
  action: 'upgraded to Scale',
  timeAgo: '1 hr ago'
}, {
  name: 'Daniel',
  location: 'Memphis',
  action: 'started free trial',
  timeAgo: '1 hr ago'
}, {
  name: 'Jennifer',
  location: 'Louisville',
  action: 'sent first 100 offers',
  timeAgo: '1 hr ago'
}, {
  name: 'Matthew',
  location: 'Milwaukee',
  action: 'closed their first deal',
  timeAgo: '1 hr ago'
}, {
  name: 'Brittany',
  location: 'Albuquerque',
  action: 'sent 500+ offers',
  timeAgo: '1 hr ago'
}];
export function ActivityNotification() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [usedIndices, setUsedIndices] = useState<number[]>([]);
  useEffect(() => {
    if (isDismissed) return;
    // Show first notification after 4 seconds
    const initialDelay = setTimeout(() => {
      showNextNotification();
    }, 4000);
    return () => clearTimeout(initialDelay);
  }, [isDismissed]);
  const showNextNotification = () => {
    if (isDismissed) return;
    // Get a random notification that hasn't been used recently
    let availableIndices = notificationPool.map((_, index) => index).filter(index => !usedIndices.includes(index));
    // Reset used indices if we've shown all notifications
    if (availableIndices.length === 0) {
      setUsedIndices([]);
      availableIndices = notificationPool.map((_, index) => index);
    }
    const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
    const notification = notificationPool[randomIndex];
    // Update used indices
    setUsedIndices(prev => [...prev, randomIndex].slice(-10)); // Keep last 10
    // Show notification
    setCurrentNotification(notification);
    setIsVisible(true);
    // Hide after 5 seconds
    setTimeout(() => {
      setIsVisible(false);
    }, 5000);
    // Show next notification after 6 seconds (1s after hiding) + random 4-8s delay
    setTimeout(() => {
      showNextNotification();
    }, 6000 + Math.random() * 4000 + 4000); // 10-14 seconds between notifications
  };
  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };
  if (!currentNotification || isDismissed) return null;
  return <div className={`fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-auto z-50 transition-all duration-500 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8 pointer-events-none'}`} data-id="element-2138">
      <div className="relative group" data-id="element-2139">
        {/* Main notification card */}
        <div className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-[#1a1a1a] border border-white/10 rounded-lg sm:rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.4)] backdrop-blur-sm w-full sm:min-w-[320px] sm:max-w-[400px]" data-id="element-2140">
          {/* Icon */}
          <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-[#3ab795]/10 flex items-center justify-center" data-id="element-2141">
            <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#3ab795]" strokeWidth={2} data-id="element-2142" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0" data-id="element-2143">
            <p className="text-xs sm:text-sm text-white leading-tight" data-id="element-2144">
              <span className="font-semibold" data-id="element-2145">{currentNotification.name}</span>
              <span className="text-gray-400" data-id="element-2146"> from </span>
              <span className="font-medium text-gray-300" data-id="element-2147">
                {currentNotification.location}
              </span>
              <span className="text-gray-400" data-id="element-2148">
                {' '}
                {currentNotification.action}{' '}
              </span>
              <span className="text-gray-500 text-[10px] sm:text-xs" data-id="element-2149">
                {currentNotification.timeAgo}
              </span>
            </p>
          </div>

          {/* Close button */}
          <button onClick={handleDismiss} className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/10 transition-all duration-200 sm:opacity-0 sm:group-hover:opacity-100" aria-label="Dismiss notification" data-id="element-2150">
            <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} data-id="element-2151" />
          </button>
        </div>

        {/* Subtle glow effect */}
        <div className="absolute inset-0 bg-[#3ab795] opacity-5 blur-xl rounded-lg sm:rounded-xl -z-10" data-id="element-2152" />
      </div>
    </div>;
}